// Desk → Actions worklist composition (issue #289).
//
// The Desk Actions view is an IM-scoped worklist assembled CLIENT-SIDE from
// existing typed endpoints — there is no dedicated Actions controller or
// contract (ADR-0003 keeps the wire a per-view presentation projection, and
// this view needs nothing the three feeder surfaces don't already serve).
//
// The feeders are:
//   - COAC elections awaiting an IM decision  (election phase `with_im`, still open)
//   - overdrawn cash rows                       (OdCashRow snapshot)
//   - agent review items needing attention      (AgentTask.status === 'review_required')
//
// "Pending Trades" has no ingestion path (there is no trade feeder), so it is
// honestly zero — never fabricated (ADR-0009).
//
// This module is the testable seam: pure functions over plain inputs, no React
// and no `Date.now()` (the caller injects `now`). The page wires the live
// hooks to it and renders the result.

import { electionPhase, isOpen } from '@/lib/coac/lifecycle';
import type { CoacActionRow, OdCashRowDto, AgentTask } from '@septima/contract';
import { formatSignedCurrency } from '@/lib/format';

// ---------------------------------------------------------------------------
// Worklist item — the flat shape the page renders. One row per outstanding
// unit of work across every feeder.
// ---------------------------------------------------------------------------

export type WorklistCategory = 'ca-election' | 'overdrawn-cash' | 'agent-review';

/** Pill variant keys mirrored from the design-system `Pill` (no import to keep this pure). */
export type WorklistSeverity = 'red' | 'amber' | 'blue';

export interface WorklistItem {
    /** Stable key derived from the source row id (prefixed by category). */
    id: string;
    category: WorklistCategory;
    /** Short category label for the severity/category pill. */
    categoryLabel: string;
    severity: WorklistSeverity;
    /** Primary line — what the IM is looking at. */
    label: string;
    /** Secondary line — context (security id, account ref, agent type). */
    detail: string;
    /** Where the CTA points. */
    href: string;
    /** CTA verb. */
    cta: string;
    /** Source timestamp (ISO) the relative label is computed from. May be null. */
    timestamp: string | null;
    /** Epoch ms used for sort ordering; `null` timestamps sort last. */
    sortMs: number | null;
}

// ---------------------------------------------------------------------------
// Feeder filters — the single source of truth for "what is outstanding".
// Both the counts and the worklist rows derive from these so the summary
// cards can never disagree with the list below them.
// ---------------------------------------------------------------------------

/**
 * CoacAction rows awaiting the IM's election decision.
 *
 * `isOpen` carries the Ops half of the rule: once Ops finalises a corporate
 * action the Desk is read-only, so the IM cannot act and the row is not
 * outstanding. The Desk sidebar badge asks the same question, so the badge and
 * this list agree.
 */
export function outstandingCoacActions(rows: CoacActionRow[]): CoacActionRow[] {
    return rows.filter((r) => electionPhase(r) === 'with_im' && isOpen(r) && !r.isDeleted);
}

/**
 * Overdrawn cash rows still outstanding. `listOverdrawn` returns the whole
 * snapshot including rows an ops user has already chased (a non-null
 * `resolutionNote` from the #48 OverdraftResolution side table), so we exclude
 * those — mirroring the Command Centre's `isUnresolvedOverdraft` so the two
 * views agree on what counts as outstanding.
 */
export function outstandingOverdrawnRows(rows: OdCashRowDto[]): OdCashRowDto[] {
    return rows.filter((r) => r.resolutionNote == null);
}

/** Agent tasks parked in the review queue needing IM sign-off. */
export function outstandingAgentReviews(tasks: AgentTask[]): AgentTask[] {
    return tasks.filter((t) => t.status === 'review_required');
}

// ---------------------------------------------------------------------------
// Worklist items — one builder for each feeder, and the shared sort.
// Newest first; rows without a timestamp sort to the bottom.
// ---------------------------------------------------------------------------

function toMs(iso: string | null): number | null {
    if (!iso) return null;
    const ms = new Date(iso).getTime();
    return Number.isNaN(ms) ? null : ms;
}

function caElectionItem(row: CoacActionRow): WorklistItem {
    const security = row.securityName ?? row.securityId ?? row.eventType;
    // CEDAR stamps an urgency rating on routing. Mirror the Command Centre's
    // caSeverity mapping exactly so the same election shows the same alert level
    // in both views: critical -> red, high -> amber, medium/low -> blue, and an
    // unrated election awaiting decision -> amber.
    const severity: WorklistSeverity =
        row.urgencyRating === 'critical'
            ? 'red'
            : row.urgencyRating === 'medium' || row.urgencyRating === 'low'
              ? 'blue'
              : 'amber';
    const timestamp = row.assignedAt ?? row.createdAt;
    return {
        id: `ca-election:${row.id}`,
        category: 'ca-election',
        categoryLabel: 'CA Election',
        severity,
        label: `${row.eventType} — ${security}`,
        detail: row.securityId ? `Security ${row.securityId}` : 'Election decision required',
        href: '/desk/corporate-actions',
        cta: 'Record decision',
        timestamp,
        sortMs: toMs(timestamp),
    };
}

function overdrawnItem(row: OdCashRowDto): WorklistItem {
    const balance = formatSignedCurrency(row.postedBalance, row.currency);
    return {
        id: `overdrawn-cash:${row.id}`,
        category: 'overdrawn-cash',
        categoryLabel: 'Overdrawn',
        severity: 'red',
        label: `${row.cashAccountName} — ${balance}`,
        detail: `${row.clientReference} · ${row.accountReference}`,
        href: '/desk/cash/overdrawn',
        cta: 'Review account',
        // The overdrawn snapshot dates the last recalculation, not an event.
        timestamp: row.lastRecalculatedOn || null,
        sortMs: toMs(row.lastRecalculatedOn || null),
    };
}

function agentReviewItem(task: AgentTask): WorklistItem {
    const label = task.clientName
        ? `${humaniseTaskType(task.type)} — ${task.clientName}`
        : humaniseTaskType(task.type);
    return {
        id: `agent-review:${task.id}`,
        category: 'agent-review',
        categoryLabel: 'Agent Review',
        severity: 'blue',
        label,
        detail: task.triggerDetail,
        href: `/desk/agents?focus=${encodeURIComponent(`task:${task.id}`)}`,
        cta: 'Review draft',
        timestamp: task.createdAt,
        sortMs: toMs(task.createdAt),
    };
}

/** The worklist items of the corporate actions that wait for the IM. */
export function caElectionItems(rows: CoacActionRow[]): WorklistItem[] {
    return outstandingCoacActions(rows).map(caElectionItem);
}

/** The worklist items of the overdrawn accounts. */
export function overdrawnItems(rows: OdCashRowDto[]): WorklistItem[] {
    return outstandingOverdrawnRows(rows).map(overdrawnItem);
}

/** The worklist items of the agent artefacts that wait for a review. */
export function agentReviewItems(tasks: AgentTask[]): WorklistItem[] {
    return outstandingAgentReviews(tasks).map(agentReviewItem);
}

/**
 * Newest first; null timestamps (no event date) sink to the bottom but keep a
 * stable order among themselves via the id tiebreak.
 */
export function sortWorklist(items: readonly WorklistItem[]): WorklistItem[] {
    return [...items].sort((a, b) => {
        if (a.sortMs === b.sortMs) return a.id.localeCompare(b.id);
        if (a.sortMs === null) return 1;
        if (b.sortMs === null) return -1;
        return b.sortMs - a.sortMs;
    });
}

// ---------------------------------------------------------------------------
// Relative-time label — clock-injected (the page passes `clockNow()`), so the
// "Xm/h/d ago" labels age forward and tests can pin the moment.
// ---------------------------------------------------------------------------

export function relativeTimeFrom(iso: string | null, now: Date): string {
    if (!iso) return '—';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '—';
    const diffMs = Math.max(0, now.getTime() - t);
    const mins = Math.round(diffMs / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// Task-type humaniser — turns the snake_case AgentTaskType into a Desk label.
// ---------------------------------------------------------------------------

export function humaniseTaskType(type: string): string {
    return type
        .split('_')
        .map((w) => (w.length === 0 ? w : w[0].toUpperCase() + w.slice(1)))
        .join(' ');
}
